/**
 * Variable resolution helpers — pure functions that translate raw author text
 * into runtime-rendered text using a {@link VariableContext}.
 *
 * Two failure modes are supported:
 *   - **`'token'` (default)**: leave the original `{{token}}` in place. Used in
 *     authoring/preview surfaces so admins can see what didn't resolve.
 *   - **`'placeholder'`**: replace with a configurable string (default empty).
 *     Used in client-facing rendering so unresolved tokens never leak as raw
 *     curly-brace text to clients.
 */
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';

import {
  getVariableDefinition,
  VARIABLE_TOKEN_REGEX,
  type VariableContext,
} from './registry';

export type UnresolvedTokenStrategy = 'token' | 'placeholder';

export interface ResolveOptions {
  /** What to do when a token doesn't have a known definition or value. */
  onUnresolved?: UnresolvedTokenStrategy;
  /** Replacement string when {@link onUnresolved} is `'placeholder'`. */
  placeholder?: string;
}

/**
 * Replace every `{{namespace.field}}` token in the given text with the
 * resolved value from `context`. Returns the original text unchanged when no
 * tokens are present — callers can call this on every string field cheaply.
 */
export function resolveVariablesInText(
  text: string | undefined,
  context: VariableContext,
  options: ResolveOptions = {},
): string {
  if (!text) return text ?? '';
  if (!text.includes('{{')) return text;

  const strategy = options.onUnresolved ?? 'token';
  const placeholder = options.placeholder ?? '';

  return text.replace(VARIABLE_TOKEN_REGEX, (match, id: string) => {
    const def = getVariableDefinition(id);
    const value = def?.resolve(context);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return strategy === 'placeholder' ? placeholder : match;
  });
}

/**
 * Deep-resolve every author-editable text field on a `Questionnaire` so the
 * returned resource is safe to hand to a renderer (preview drawer or client
 * portal). Non-text fields (codings, types, etc.) are not modified.
 */
export function resolveQuestionnaireVariables(
  questionnaire: Questionnaire,
  context: VariableContext,
  options: ResolveOptions = {},
): Questionnaire {
  const resolveText = (text: string | undefined): string | undefined => {
    if (text === undefined) return undefined;
    return resolveVariablesInText(text, context, options);
  };

  const resolveItem = (item: QuestionnaireItem): QuestionnaireItem => {
    const next: QuestionnaireItem = { ...item };
    if (item.text !== undefined) next.text = resolveText(item.text);
    if (item.prefix !== undefined) next.prefix = resolveText(item.prefix);
    if (item.initial?.length) {
      next.initial = item.initial.map((init) => {
        if (init.valueString !== undefined) {
          const resolved = resolveText(init.valueString);
          return resolved !== init.valueString ? { ...init, valueString: resolved } : init;
        }
        return init;
      });
    }
    if (item.item) next.item = item.item.map(resolveItem);
    return next;
  };

  return {
    ...questionnaire,
    title: resolveText(questionnaire.title) ?? questionnaire.title,
    description: resolveText(questionnaire.description) ?? questionnaire.description,
    purpose: resolveText(questionnaire.purpose) ?? questionnaire.purpose,
    item: questionnaire.item ? questionnaire.item.map(resolveItem) : questionnaire.item,
  };
}

/** Returns the set of variable ids referenced by the given text. */
export function listReferencedVariables(text: string | undefined): string[] {
  if (!text) return [];
  const ids = new Set<string>();
  for (const match of text.matchAll(VARIABLE_TOKEN_REGEX)) {
    ids.add(match[1]);
  }
  return [...ids];
}
